// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
//
// The onderbouwing of one effect, taken from the same published meetstandaard
// document its euro values are calculated with.
//
// Needed because the separate /monetarisering/onderbouwing document is an
// unversioned demo (5 effects, keyed on the old /database ids). For an effect
// adopted from a versioned standaard it showed the amounts of another version
// than the one the app calculates with — found 2026-11-03 at Vitaminds after
// the arbeidsparticipatie 1.1 migration: −€ 15.000 shown, −€ 17.346 used.
// The versioned document carries the full onderbouwing per level itself
// (posts per stakeholder that sum to the level, situatieschets, source).
//
// Output has the shape MsiOnderbouwingModal already renders
// (buildOnderbouwingIndex's OnderbouwingEffect), with free-text labels on the
// posts so the modal does not humanize them as enum codes.

/**
 * @typedef {import('./buildOnderbouwingIndex').OnderbouwingEffect} OnderbouwingEffect
 */

/**
 * Whether an effect's onderbouwing comes from its versioned document: it
 * carries a `sector:slug` uid and its rows are stamped with a version.
 *
 * @param {string|null|undefined} msiId - OutputEffects.MSIId
 * @param {string|null|undefined} standaardVersion - Version the effect is valued at
 * @returns {boolean}
 */
export const isVersionedMsiEffect = (msiId, standaardVersion) =>
    typeof msiId === 'string' && msiId.includes(':') && typeof standaardVersion === 'string' && standaardVersion !== ''

const asNumberOrNull = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null)

const asList = (value) => {
    if (Array.isArray(value)) return value.filter((item) => typeof item === 'string' && item.trim() !== '')
    if (typeof value === 'string' && value.trim() !== '') return [value]
    return []
}

/**
 * @param {object} proxy - One post (`proxies[]`) of a monetarisering niveau
 * @param {string} key - Stable key for the source entry
 */
const adaptPost = (proxy, key) => ({
    component: proxy.proxy || '',
    label: proxy.proxy || '',
    stakeholder: proxy.stakeholder || '',
    stakeholderLabel: proxy.stakeholder || '',
    bedrag: asNumberOrNull(proxy.bedrag),
    berekening: proxy.berekening || '',
    aannames: asList(proxy.aannames),
    bronnen: proxy.bronBedrag
        ? [{ id: key, type: 'literatuur', title: proxy.bronBedrag, url: null, note: null }]
        : [],
})

/**
 * @param {object|null|undefined} doc - A published meetstandaard document (`/meetstandaard/{sector}/{version}`)
 * @param {string} msiId - The effect's uid (`sector:slug`)
 * @returns {{entry: OnderbouwingEffect, meta: {title: string, version: string, toelichting: null}}|null}
 *   null when the effect is absent or has no amounts
 */
const adaptStandaardOnderbouwing = (doc, msiId) => {
    const effecten = Array.isArray(doc?.effecten) ? doc.effecten : []
    const sector = doc?.meta?.sector || ''
    const effect = effecten.find((e) => (e.uid || `${sector}:${e.slug}`) === msiId)
    const niveaus = Array.isArray(effect?.monetarisering?.niveaus) ? effect.monetarisering.niveaus : []
    if (!effect || niveaus.length === 0) return null

    const schetsen = Array.isArray(effect.situatieschetsen) ? effect.situatieschetsen : []
    const schetsVoor = (score) => {
        const schets = schetsen.find((s) => Number(s.score) === score)
        return schets ? schets.situatieschets || '' : ''
    }

    const adapted = niveaus.map((niveau) => {
        const score = Number(niveau.score ?? niveau.niveau)
        const posten = Array.isArray(niveau.proxies) ? niveau.proxies : []
        return {
            score,
            situatie: schetsVoor(score),
            waardePerJaar: asNumberOrNull(niveau.totaleWaardeIndicatief),
            literatuur: null,
            opbouw: posten.length > 0
                ? posten.map((post, index) => adaptPost(post, `${effect.slug}-${score}-${index}`))
                : null,
            bronnen: [],
        }
    })

    // Volledig when every level with an amount other than zero is broken down
    // into posts that carry an amount.
    const volledig = adapted.every((n) => n.waardePerJaar === 0
        || (n.waardePerJaar !== null && Array.isArray(n.opbouw) && n.opbouw.every((post) => post.bedrag !== null)))

    return {
        entry: {
            id: effect.slug,
            effectId: msiId,
            effect: effect.effect,
            categorie: effect.categorie || '',
            status: volledig ? 'volledig' : 'gedeeltelijk',
            methodiek: '',
            prijsbasis: [],
            niveaus: adapted,
        },
        meta: {
            title: `Meetstandaard ${doc.meta?.sectorLabel || sector}`,
            version: doc.meta?.version || '',
            toelichting: null,
        },
    }
}

export default adaptStandaardOnderbouwing
